<template>
  <div>
    <va-modal
      id="requestDetailModal"
      ref="requestDetailsModal"
      :value="isOpen"
      size="medium"
      @cancel="onCancel"
      :hideDefaultActions="true"
    >
      <div slot="header" class="modal-title d-flex justify-content-between mb-4">
        <span>{{ $t('evocall.requests.details_title') }}</span>
      </div>

      <div class="modal-header-actions d-flex justify-content-end">
        <va-button
          class="va-button-rounded priority-icon-wrapper"
          :class="`priority--${request.priority.name.toLowerCase()}`"
          :disabled="!userCanUpdateRequestPriorityOrCancel"
          @click="onClickUpdatePriority"
        >
          <span class="is-bold">{{ $t('evocall.requests.priority')}}: {{ $t(`evocall.priorities.${request.priority.id}`, request.priority.name) }}</span>
        </va-button>

        <va-popover
          placement="bottom"
          :message="$t('evocal.requests.cancel_request')"
          v-if="userCanUpdateRequestPriorityOrCancel"
        >
          <va-button
            large
            class="va-button-rounded"
            icon="i-phone-slash-white"
            color="gray"
            @click="onClickCancelRequest"
          >
          </va-button>
        </va-popover>
      </div>

      <div class="row">
        <div class="col-12">
          <i class="fa fa-clock-o is-bold" style="margin-right: 2px; margin-bottom: 0.7rem;"></i>
          <span class="is-bold"> {{ moment(request.createdAt).format('DD MMMM YYYY [&nbsp;] HH:mm') }}</span>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-2">
          <span>{{$t('agenda.appointments.facility')}}:&nbsp;</span>
          <span class="is-bold">{{request.facilityName ? request.facilityName : 'N/A'}}</span>
        </div>

        <div class="col-12 mb-2">
          <span v-if="request.acceptedBy !== null">{{ $t('clinical.appointments.audiologist') }}:</span>
          <span v-else>{{ $t('clinical.appointments.dispenser') }}:</span>
          &nbsp;
          <span class="is-bold" v-if="request.dispenserNickname">
          <span>{{ request.dispenserNickname }}</span>
        </span>
          <span class="is-bold" v-else>N/A</span>
        </div>

        <div class="col-12 mb-2">
          <span>{{$t('agenda.appointments.pacient')}}:&nbsp;</span>
          <span class="is-bold">{{request.customerName ? request.customerName : 'N/A'}}</span>
        </div>
        <div class="col-12 mb-2">
          <span>{{$t('clinical.people.client_nr')}}:&nbsp;</span>
          <span class="is-bold">{{request.customerNumber ? request.customerNumber : 'N/A'}}</span>
        </div>
      </div>
      <div slot="actions" v-if="request && userCanAcceptRequest">
        <div class="d-flex flex-column align-items-end">
          <va-button
            large
            class="px-4 py-3"
            @click="submit">
            {{ $t('evocall.requests.start') }}
          </va-button>
          <div>
            <small v-if="inBreak">{{ $t('evocall.requests.break_override_warning_1') }}</small>
            <small v-if="inBreak">{{ $t('evocall.requests.break_override_warning_2') }}</small>
          </div>
        </div>
      </div>

    </va-modal>
    <PriorityForm
      v-if="isUpdatingPriority"
      :request="request"
      @modal-closed="onPriorityModalClosed"
      @priority-updated="onPriorityUpdated"
    />
    <CancelCallRequestModal
      v-if="isCancelingRequest"
      :request="request"
      @request-canceled="onRequestCanceled"
      @operation-not-permited="onRequestCancelOperationNotPermited"
      @modalClosed="onCancelRequestModalClosed"
    />
  </div>
</template>
<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {BreaksService, RequestService} from "@/api/VirtualWaitingRoomService";
import PriorityForm from "@/components/virtual_waiting_room/waiting_room/PriorityForm";
import CancelCallRequestModal from "@/components/virtual_waiting_room/dispenser_dashboard/CancelCallRequestModal";
import EventBus from "@/utils/EventBus";

  export default {
    components: {CancelCallRequestModal, PriorityForm},
    props: {
      request: {
        type: Object,
        required: true
      },
      serviceSlug: {
        type: String,
        required: true
      },
      component: {
        type: Object,
        required: true
      },
      waitingRoomModuleKey: {
        type: String,
        required: true
      },
    },
    data () {
      return {
        isOpen: false,
        isUpdatingPriority: false,
        isCancelingRequest: false,
        wasCanceledByUser: false,
      }
    },
    mounted () {
      this.isOpen = true
      this.loadAuthUserEntityActivityRecords()
    },
    created() {
      EventBus.$on('modalClosed',  () =>{
        this.onCancelRequestModalClosed()
      })
    },
    methods: {
      ...mapActions('auth', {
        loadAuthUserEntityActivityRecords: 'LOAD_AUTH_USER_ENTITY_ACTIVITIES'
      }),
      onCancel: function () {
        this.closeModal()
      },
      closeModal: function () {
        /*if (!this.wasCanceledByUser) {
          this.$socket.emit('REQUEST_CLOSED', { serviceId: this.request.service_id, requestRow: this.request })
        }*/
        this.isOpen = false
        setTimeout(() => {
          this.$emit('modal-closed')
        }, 500)
      },
      submit: function () {
        let wasInBreak = this.inBreak
        if(this.userCanAcceptRequest) {
          let promisesToExecute = []

          if (this.inBreak) {
            let endBreakPromise = BreaksService.stop(this.activeBreak.id).then(() => {
            }).catch((error) => {
              this.showToast(this.$t('evocall.requests.something_went_wrong'), this.$store.state.errorToastOptions)
            })

            promisesToExecute.push(endBreakPromise)
          }



          let acceptCallPromise = RequestService.accept(this.request.id)
            .then(request => {
              this.showToast(wasInBreak ? this.$t('evocall.requests.break_overridden') : this.$t('evocall.requests.actions.accepted'), this.$store.state.successToastOptions)
              this.closeModal()
            })
            .catch(errors => {
              if(errors.response.status === 422) {
                this.showToast(this.$t('evocall.requests.actions.request_ongoing'), this.$store.state.errorToastOptions)
              }
            })

          promisesToExecute.push(acceptCallPromise)

          Promise.all(promisesToExecute)
        }
      },
      onClickUpdatePriority: function () {
        if (this.userCanUpdateRequestPriorityOrCancel) {
          this.isOpen = false
          setTimeout(() => {
            this.isUpdatingPriority = true
          }, 500)
        }
      },
      onPriorityModalClosed: function () {
        this.isUpdatingPriority = false
        this.isOpen = true
      },
      onPriorityUpdated: function (eventPayload) {
        this.$emit('priority-updated', eventPayload)
      },
      onClickCancelRequest: function () {
        this.isOpen = false
        setTimeout(() => {
          this.wasCanceledByUser = true
          this.isCancelingRequest = true
        }, 500)
      },
      onRequestCanceled: function (eventPayload) {
        this.closeModal()
      },
      onRequestCancelOperationNotPermited: function () {
        this.closeModal()
      },
      onCancelRequestModalClosed: function () {
        this.isCancelingRequest = false
        this.wasCanceledByUser = false
        this.closeModal()
      },
    },
    computed: {
      ...mapGetters('auth', [
        'userIsActiveAudiologist'
      ]),
      ...mapState({
        inBreak (state, getters) {
          return getters[`${this.waitingRoomModuleKey}/inBreak`]
        },
        activeRequest(state, getters) {
          return getters[`${this.waitingRoomModuleKey}/activeRequest`]
        },
        activeBreak(state, getters) {
          return getters[`${this.waitingRoomModuleKey}/activeBreak`]
        },
        infoTypes(state, getters) {
          return getters[`${this.waitingRoomModuleKey}/infoTypes`]
        },
        accessTypes(state, getters) {
          return getters[`${this.waitingRoomModuleKey}/accessTypes`]
        }
      }),
      virtualWaitingRoomService: function () {
        return this.$store.state.services.find(s => s.url === this.$store.state.virtual_waiting_room_server)
      },
      userCanUpdateRequestPriorityOrCancel: function () {
        return this.virtualWaitingRoomService.user_permissions.some(permission => permission.info_type === this.infoTypes.REQUESTS && permission.access_type >= this.accessTypes.ADMIN && (permission.entity_id === null || permission.entity_id === this.request.entityId || permission.entity_id === this.request.audProviderId))
      },
      userCanAcceptRequest: function () {
        //verify if is audiologist
        //verify if has no request accepted already
        //verify its not a supervisor
        return this.userIsActiveAudiologist && this.activeRequest === null
      }
    }
  }
</script>

<style lang="scss">

#requestDetailsModal {
  .va-modal__actions {
    justify-content: flex-end;
  }
}

</style>
